import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Trash2, Archive, RefreshCw, User, AlertTriangle, CheckCircle,
  FileText, Activity, Brain, Calendar, Database, Shield, 
  ChevronDown, ChevronUp, Clock, Settings, XCircle, Eye
} from 'lucide-react';

const API = '/api';

function StatCard({ title, value, subValue, icon: Icon, color = 'text-white/70' }) {
  return (
    <div className="bg-adm-surface/[0.02] border border-white/[0.06] rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-adm-surface/5 flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-2xl font-bold text-white/90">{value}</div>
          <div className="text-xs text-white/40">{title}</div>
          {subValue && <div className="text-[10px] text-white/30">{subValue}</div>}
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({ open, onClose, onConfirm, title, message, destructive = false }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0f] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {destructive && <AlertTriangle className="w-5 h-5 text-red-400" />}
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-white/60">{message}</p>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-white/50">Cancel</Button>
          <Button 
            onClick={onConfirm} 
            className={destructive ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-[#C89B7B] hover:bg-[#b88a6a] text-black'}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DataSection({ title, icon: Icon, children, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <Card className="bg-adm-surface/[0.02] border-white/[0.06] mb-4">
      <CardHeader className="py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <CardTitle className="text-sm text-white/70 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#C89B7B]" />
            {title}
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
        </CardTitle>
      </CardHeader>
      {expanded && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  );
}

function ItemRow({ item, type, onAction, loading }) {
  const statusColors = {
    published: 'bg-emerald-500/20 text-emerald-400',
    active: 'bg-emerald-500/20 text-emerald-400',
    draft: 'bg-blue-500/20 text-blue-400',
    archived: 'bg-adm-surface/10 text-white/40'
  };
  
  const status = item.status || (item.is_active ? 'active' : 'inactive');
  
  return (
    <div className="flex items-center justify-between py-2 px-3 bg-adm-surface/[0.01] rounded-lg mb-2 border border-white/[0.04]">
      <div className="flex items-center gap-3">
        <Badge className={`text-[9px] ${statusColors[status] || 'bg-adm-surface/10 text-white/40'}`}>
          {status}
        </Badge>
        <div>
          <div className="text-sm text-white/80">{item.title || item.phase_name || item.id}</div>
          {item.created_at && (
            <div className="text-[10px] text-white/30">
              {new Date(item.created_at).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {status === 'draft' && type !== 'execution' && (
          <Button 
            size="sm" variant="ghost" 
            onClick={() => onAction('publish', item.id)}
            disabled={loading}
            className="h-7 text-xs text-emerald-400 hover:text-emerald-300"
          >
            <CheckCircle className="w-3 h-3 mr-1" /> Publish
          </Button>
        )}
        {status === 'published' && (
          <Button 
            size="sm" variant="ghost" 
            onClick={() => onAction('unpublish', item.id)}
            disabled={loading}
            className="h-7 text-xs text-amber-400 hover:text-amber-300"
          >
            <XCircle className="w-3 h-3 mr-1" /> Unpublish
          </Button>
        )}
        <Button 
          size="sm" variant="ghost" 
          onClick={() => onAction('archive', item.id)}
          disabled={loading}
          className="h-7 text-xs text-white/40 hover:text-white/60"
        >
          <Archive className="w-3 h-3 mr-1" /> Archive
        </Button>
        <Button 
          size="sm" variant="ghost" 
          onClick={() => onAction('delete', item.id)}
          disabled={loading}
          className="h-7 text-xs text-red-400 hover:text-red-300"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

export default function DataManagement() {
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscriber, setSelectedSubscriber] = useState('');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Detail data
  const [decodes, setDecodes] = useState([]);
  const [phases, setPhases] = useState([]);
  const [reports, setReports] = useState([]);
  const [execLogs, setExecLogs] = useState([]);
  const [mindfloLogs, setMindfloLogs] = useState([]);
  
  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  
  // Date range inputs
  const [clearStartDate, setClearStartDate] = useState('');
  const [clearEndDate, setClearEndDate] = useState('');
  
  const token = localStorage.getItem('rf_access_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  
  // Fetch subscribers
  useEffect(() => {
    fetch(`${API}/admin/correlation-subscribers`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.subscribers) {
          setSubscribers(d.subscribers);
          if (d.subscribers.length > 0) setSelectedSubscriber(d.subscribers[0].id);
        }
      });
  }, []);
  
  // Fetch overview
  const fetchOverview = useCallback(async () => {
    if (!selectedSubscriber) return;
    setLoading(true);
    
    try {
      const [overviewRes, decodesRes, phasesRes, reportsRes, execRes, mindfloRes] = await Promise.all([
        fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/overview`, { headers }),
        fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/decodes`, { headers }),
        fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/phases`, { headers }),
        fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/reports`, { headers }),
        fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/execution-logs?days=30`, { headers }),
        fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/mindflo-entries?days=30`, { headers })
      ]);
      
      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (decodesRes.ok) setDecodes((await decodesRes.json()).decodes || []);
      if (phasesRes.ok) setPhases((await phasesRes.json()).phases || []);
      if (reportsRes.ok) setReports((await reportsRes.json()).reports || []);
      if (execRes.ok) setExecLogs((await execRes.json()).dates || []);
      if (mindfloRes.ok) setMindfloLogs((await mindfloRes.json()).dates || []);
    } catch (e) {
      console.error('Error fetching data:', e);
    }
    setLoading(false);
  }, [selectedSubscriber]);
  
  useEffect(() => {
    if (selectedSubscriber) fetchOverview();
  }, [selectedSubscriber, fetchOverview]);
  
  // Action handlers
  const handleDecodeAction = async (action, id) => {
    if (action === 'delete') {
      setConfirmDialog({
        open: true,
        title: 'Delete DECODE',
        message: 'This will permanently delete this DECODE. This cannot be undone.',
        destructive: true,
        onConfirm: async () => {
          setActionLoading(true);
          await fetch(`${API}/admin/data-management/decodes/${id}?force=true`, { method: 'DELETE', headers });
          fetchOverview();
          setActionLoading(false);
          setConfirmDialog({ open: false });
        }
      });
      return;
    }
    
    setActionLoading(true);
    await fetch(`${API}/admin/data-management/decodes/${id}/${action}`, { method: 'PUT', headers });
    fetchOverview();
    setActionLoading(false);
  };
  
  const handlePhaseAction = async (action, id) => {
    if (action === 'delete') {
      setConfirmDialog({
        open: true,
        title: 'Delete Phase',
        message: 'This will permanently delete this phase and all related data (schedule items, execution logs). This cannot be undone.',
        destructive: true,
        onConfirm: async () => {
          setActionLoading(true);
          await fetch(`${API}/admin/data-management/phases/${id}?delete_related=true&force=true`, { method: 'DELETE', headers });
          fetchOverview();
          setActionLoading(false);
          setConfirmDialog({ open: false });
        }
      });
      return;
    }
    
    setActionLoading(true);
    const endpoint = action === 'publish' ? 'activate' : action;
    await fetch(`${API}/admin/data-management/phases/${id}/${endpoint}`, { method: 'PUT', headers });
    fetchOverview();
    setActionLoading(false);
  };
  
  const handleReportAction = async (action, id) => {
    if (action === 'delete') {
      setConfirmDialog({
        open: true,
        title: 'Delete Report',
        message: 'This will permanently delete this report. This cannot be undone.',
        destructive: true,
        onConfirm: async () => {
          setActionLoading(true);
          await fetch(`${API}/admin/data-management/reports/${id}`, { method: 'DELETE', headers });
          fetchOverview();
          setActionLoading(false);
          setConfirmDialog({ open: false });
        }
      });
      return;
    }
    
    setActionLoading(true);
    await fetch(`${API}/admin/data-management/reports/${id}/${action}`, { method: 'PUT', headers });
    fetchOverview();
    setActionLoading(false);
  };
  
  const handleClearExecutionLogs = async (type) => {
    if (type === 'all') {
      setConfirmDialog({
        open: true,
        title: 'Clear ALL Execution Logs',
        message: `This will permanently delete ALL ${overview?.execution_logs?.total || 0} execution logs. This cannot be undone.`,
        destructive: true,
        onConfirm: async () => {
          setActionLoading(true);
          await fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/execution-logs/clear-all?confirm=true`, { method: 'DELETE', headers });
          fetchOverview();
          setActionLoading(false);
          setConfirmDialog({ open: false });
        }
      });
    } else if (type === 'range' && clearStartDate && clearEndDate) {
      setConfirmDialog({
        open: true,
        title: 'Clear Execution Logs by Range',
        message: `This will delete execution logs from ${clearStartDate} to ${clearEndDate}.`,
        destructive: true,
        onConfirm: async () => {
          setActionLoading(true);
          await fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/execution-logs/clear-range?start_date=${clearStartDate}&end_date=${clearEndDate}`, { method: 'DELETE', headers });
          fetchOverview();
          setActionLoading(false);
          setConfirmDialog({ open: false });
        }
      });
    }
  };
  
  const handleClearMindflo = async (type) => {
    if (type === 'all') {
      setConfirmDialog({
        open: true,
        title: 'Clear ALL MindFlō Entries',
        message: `This will permanently delete ALL ${overview?.mindflo?.total || 0} MindFlō entries. This cannot be undone.`,
        destructive: true,
        onConfirm: async () => {
          setActionLoading(true);
          await fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/mindflo/clear-all?confirm=true`, { method: 'DELETE', headers });
          fetchOverview();
          setActionLoading(false);
          setConfirmDialog({ open: false });
        }
      });
    } else if (type === 'range' && clearStartDate && clearEndDate) {
      setConfirmDialog({
        open: true,
        title: 'Clear MindFlō Entries by Range',
        message: `This will delete MindFlō entries from ${clearStartDate} to ${clearEndDate}.`,
        destructive: true,
        onConfirm: async () => {
          setActionLoading(true);
          await fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/mindflo/clear-range?start_date=${clearStartDate}&end_date=${clearEndDate}`, { method: 'DELETE', headers });
          fetchOverview();
          setActionLoading(false);
          setConfirmDialog({ open: false });
        }
      });
    }
  };
  
  const handleResetAll = () => {
    setConfirmDialog({
      open: true,
      title: 'Reset Subscriber Data',
      message: 'This will clear all execution logs, MindFlō entries, and archive draft reports. DECODE and Phase will be kept.',
      destructive: true,
      onConfirm: async () => {
        setActionLoading(true);
        await fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/reset-all-data?confirm=true`, { method: 'DELETE', headers });
        fetchOverview();
        setActionLoading(false);
        setConfirmDialog({ open: false });
      }
    });
  };
  
  return (
    <AdminLayout>
      <div className="p-6 max-w-[1200px] mx-auto" data-testid="data-management">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-white/90 flex items-center gap-2">
              <Database className="w-6 h-6 text-[#C89B7B]" />
              Subscriber Data Management
            </h1>
            <p className="text-xs text-white/40 mt-1">
              Safe cleanup and data governance controls
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedSubscriber} onValueChange={setSelectedSubscriber}>
              <SelectTrigger className="w-[220px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs" data-testid="subscriber-select">
                <User className="w-3 h-3 mr-2 text-white/40" />
                <SelectValue placeholder="Select subscriber" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                {subscribers.map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-white text-xs">
                    {s.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button onClick={fetchOverview} size="sm" variant="ghost" className="text-xs text-white/50 h-9">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
            
            <Button onClick={handleResetAll} size="sm" variant="outline" className="text-xs border-red-500/30 text-red-400 h-9">
              <AlertTriangle className="w-3 h-3 mr-1" /> Reset Data
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-64 text-white/40">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading...
          </div>
        ) : !overview ? (
          <div className="text-center py-12 text-white/40">
            <User className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>Select a subscriber to manage data</p>
          </div>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-6 gap-4 mb-6">
              <StatCard 
                title="Published DECODE" 
                value={overview.decode?.published || 0} 
                subValue={overview.decode?.current?.id ? 'Active' : 'None'}
                icon={Shield}
                color="text-emerald-400"
              />
              <StatCard 
                title="Active Phase" 
                value={overview.phases?.active || 0}
                subValue={overview.phases?.current?.phase_name || 'None'}
                icon={Activity}
                color="text-blue-400"
              />
              <StatCard 
                title="Schedule Items" 
                value={overview.schedule_items?.active || 0}
                subValue={`${overview.schedule_items?.inactive || 0} inactive`}
                icon={Calendar}
                color="text-amber-400"
              />
              <StatCard 
                title="Execution Logs" 
                value={overview.execution_logs?.total || 0}
                subValue={overview.execution_logs?.oldest_date || 'No data'}
                icon={Clock}
              />
              <StatCard 
                title="MindFlō Entries" 
                value={overview.mindflo?.total || 0}
                subValue={`${overview.mindflo?.today || 0} today`}
                icon={Brain}
                color="text-purple-400"
              />
              <StatCard 
                title="Reports" 
                value={overview.reports?.published || 0}
                subValue={`${overview.reports?.draft || 0} drafts`}
                icon={FileText}
              />
            </div>
            
            {/* Date Range Controls */}
            <Card className="bg-adm-surface/[0.02] border-white/[0.06] mb-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <span className="text-xs text-white/50">Date Range for Bulk Clear:</span>
                  <Input 
                    type="date" 
                    value={clearStartDate} 
                    onChange={e => setClearStartDate(e.target.value)}
                    className="w-[150px] bg-adm-surface/5 border-white/10 text-white h-8 text-xs"
                    placeholder="Start Date"
                  />
                  <span className="text-white/30">to</span>
                  <Input 
                    type="date" 
                    value={clearEndDate} 
                    onChange={e => setClearEndDate(e.target.value)}
                    className="w-[150px] bg-adm-surface/5 border-white/10 text-white h-8 text-xs"
                    placeholder="End Date"
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* DECODE Section */}
            <DataSection title="DECODE" icon={Shield}>
              {decodes.length === 0 ? (
                <p className="text-xs text-white/40">No DECODEs found</p>
              ) : (
                decodes.map(d => (
                  <ItemRow key={d.id} item={{...d, title: `DECODE v${d.version || 1}`}} type="decode" onAction={handleDecodeAction} loading={actionLoading} />
                ))
              )}
            </DataSection>
            
            {/* Phases Section */}
            <DataSection title="Phases / Protocols" icon={Activity}>
              {phases.length === 0 ? (
                <p className="text-xs text-white/40">No phases found</p>
              ) : (
                phases.map(p => (
                  <ItemRow key={p.id} item={{...p, title: p.phase_name}} type="phase" onAction={handlePhaseAction} loading={actionLoading} />
                ))
              )}
            </DataSection>
            
            {/* Execution Logs Section */}
            <DataSection title="Execution Logs" icon={Clock}>
              <div className="flex items-center gap-2 mb-3">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleClearExecutionLogs('range')}
                  disabled={!clearStartDate || !clearEndDate || actionLoading}
                  className="text-xs border-amber-500/30 text-amber-400"
                >
                  Clear Date Range
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleClearExecutionLogs('all')}
                  disabled={actionLoading}
                  className="text-xs border-red-500/30 text-red-400"
                >
                  Clear All Logs
                </Button>
              </div>
              {execLogs.length === 0 ? (
                <p className="text-xs text-white/40">No execution logs found</p>
              ) : (
                <div className="max-h-[200px] overflow-y-auto">
                  {execLogs.slice(0, 10).map(log => (
                    <div key={log.date} className="flex items-center justify-between py-2 px-3 bg-adm-surface/[0.01] rounded-lg mb-1 text-xs">
                      <span className="text-white/60">{log.date}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-emerald-400">{log.as_scheduled} ✓</span>
                        <span className="text-amber-400">{log.changed} Δ</span>
                        <span className="text-red-400">{log.skipped} ✗</span>
                        <span className="text-white/40">({log.total} total)</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DataSection>
            
            {/* MindFlō Section */}
            <DataSection title="MindFlō Entries" icon={Brain}>
              <div className="flex items-center gap-2 mb-3">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleClearMindflo('range')}
                  disabled={!clearStartDate || !clearEndDate || actionLoading}
                  className="text-xs border-amber-500/30 text-amber-400"
                >
                  Clear Date Range
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleClearMindflo('all')}
                  disabled={actionLoading}
                  className="text-xs border-red-500/30 text-red-400"
                >
                  Clear All Entries
                </Button>
              </div>
              {mindfloLogs.length === 0 ? (
                <p className="text-xs text-white/40">No MindFlō entries found</p>
              ) : (
                <div className="max-h-[200px] overflow-y-auto">
                  {mindfloLogs.slice(0, 10).map(log => (
                    <div key={log.date} className="flex items-center justify-between py-2 px-3 bg-adm-surface/[0.01] rounded-lg mb-1 text-xs">
                      <span className="text-white/60">{log.date}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-white/40">{log.count} entries</span>
                        <span className="text-blue-400">NSS: {log.avg_nss}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DataSection>
            
            {/* Reports Section */}
            <DataSection title="Reports" icon={FileText}>
              <div className="flex items-center gap-2 mb-3">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={async () => {
                    setActionLoading(true);
                    await fetch(`${API}/admin/data-management/subscriber/${selectedSubscriber}/reports/archive-all-drafts`, { method: 'DELETE', headers });
                    fetchOverview();
                    setActionLoading(false);
                  }}
                  disabled={actionLoading}
                  className="text-xs border-white/20 text-white/50"
                >
                  Archive All Drafts
                </Button>
              </div>
              {reports.length === 0 ? (
                <p className="text-xs text-white/40">No reports found</p>
              ) : (
                reports.map(r => (
                  <ItemRow key={r.id} item={{...r, title: r.title || `${r.report_type} Report`}} type="report" onAction={handleReportAction} loading={actionLoading} />
                ))
              )}
            </DataSection>
          </>
        )}
        
        <ConfirmDialog 
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ open: false })}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          message={confirmDialog.message}
          destructive={confirmDialog.destructive}
        />
      </div>
    </AdminLayout>
  );
}
